import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import styles from './SettlementModal.module.css';
import { declareResult, voidBet } from '../services/api';

const MARKETS_FOR_FANCY = [
  "1st Innings 6 Overs Line",
  "2nd Innings 6 Overs Line",
  "3rd Innings 6 Overs Line",
  "1st Innings 50 Overs Line",
  "2nd Innings 50 Overs Line",
  "3rd Innings 50 Overs Line",
  "1st Innings 40 Overs Line",
  "2nd Innings 40 Overs Line",
  "3rd Innings 40 Overs Line",
  "1st Innings 30 Overs Line",
  "2nd Innings 30 Overs Line",
  "3rd Innings 30 Overs Line",
  "1st Innings 20 Overs Line",
  "2nd Innings 20 Overs Line",
  "3rd Innings 20 Overs Line",
  "1st Innings 10 Overs Line",
  "2nd Innings 10 Overs Line",
  "3rd Innings 10 Overs Line",
  "Over By Over",
  "Ball By Ball",
  "Normal",
  "khado",
  "meter",
  "fancy1",
  "oddeven",
];

/**
 * FancySettlementModal – Declare (Winner id when counts===1, or Team/Draw selection when counts 2/3) or Void.
 * Props: show, onHide, mode ('declare' | 'void'), row { matchTitle, marketType, matchId, eventId, counts, bets?, ... }, onSubmit
 */
const FancySettlementModal = ({ show, onHide, mode, row, onSubmit }) => {
  const matchTitle = row?.eventName || row?.matchTitle || '';
  const marketType = row?.marketType ?? '';
  const counts = row?.counts ?? 1;
  const firstBet = row?.bets?.[0];
  const runners = firstBet?.runners || null;
  const useTeamSelection = mode === 'declare' && !MARKETS_FOR_FANCY.includes(marketType) && counts >= 2;
  const isBinaryFancy = mode === 'declare' && ['fancy1', 'oddeven'].includes(marketType);
  const isThreeWay = counts >= 3;
  const teamOne = row?.teamOne || firstBet?.teamOne || 'Team 1';
  const teamTwo = row?.teamTwo || firstBet?.teamTwo || 'Team 2';

  const [winnerId, setWinnerId] = useState('');
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setWinnerId('');
      setSelectedOutcome(null);
      setSubmitting(false);
    }
  }, [show, mode, row]);

  const selectedDisplay = selectedOutcome;

  const handleSubmit = async () => {
    if (mode === 'declare') {
      if (useTeamSelection && selectedOutcome == null) return;
      if (!useTeamSelection && !winnerId.trim()) return;
    }
    setSubmitting(true);
    try {
      let payload;
      if (mode === 'void') {
        const payload = {
          eventid: row.eventId,
          match_id: row.matchId,
          market_type: marketType,
          gametype: row.gameType || 'FAN',
        };

        if (MARKETS_FOR_FANCY.includes(marketType)) {
          payload.selection_name = row.selectionName;
        }

        await voidBet(payload);
        await onSubmit?.({ success: true });
      } else {
        const common = {
          eventid: row.eventId,
          match_id: row.matchId,
          match_title: row.matchTitle,
          game_type: row.gameType || 'FAN',
          market_type: marketType
        };

        if (useTeamSelection) {
          payload = { ...common, winnerName: selectedOutcome };
        } else {
          if (['fancy1', 'oddeven'].includes(marketType)) {
            payload = {
              ...common,
              winnerName: winnerId.trim(),
              fancyName: row.selectionName
            };
          } else if (MARKETS_FOR_FANCY.includes(marketType)) {
            payload = {
              ...common,
              winnerId: winnerId.trim(),
              fancyName: row.selectionName
            };
          } else {
            payload = {
              ...common,
              winnerName: winnerId.trim()
            };
          }
        }

        await declareResult(payload);
        await onSubmit?.({ success: true });
      }
      onHide?.();
    } catch (e) {
      console.error(e);
      // Optional: alert user
      alert(e.response?.data?.error || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode === 'void' ? 'Void Match' : 'Declare Result';
  const isDeclareReady = useTeamSelection ? selectedOutcome != null : winnerId.trim() !== '';
  const isSubmitDisabled = (mode === 'declare' && !isDeclareReady) || submitting;

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    handleSubmit();
  };

  return (
    <Dialog.Root open={show} onOpenChange={(open) => { if (!open) onHide?.(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.radixOverlay} />
        <Dialog.Content className={styles.radixContent} aria-describedby={undefined}>
          <div style={{ background: 'transparent', border: 0 }}>
            <header className={`modal-header ${styles.modalHeader}`}>
              <Dialog.Title className={styles.modalTitle}>{title}</Dialog.Title>
              <Dialog.Close className={`close ${styles.close}`} aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </Dialog.Close>
            </header>
            <form onSubmit={handleFormSubmit} className={styles.formWrap}>
              <div className={`modal-body ${styles.modalBody}`}>
                <div className={styles.contentTitle}>
                  {marketType ? `${marketType} — ${matchTitle}` : matchTitle}
                </div>

                {mode === 'declare' && useTeamSelection && (
                  <>
                    {runners && runners.length > 0 ? (
                      <>
                        <div className={styles.outcomeRow}>
                          {runners.map((runner, idx) => (
                            <div
                              key={idx}
                              className={`${styles.outcomeBox} ${selectedOutcome === runner ? styles.selected : ''}`}
                              onClick={() => setSelectedOutcome(runner)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => e.key === 'Enter' && setSelectedOutcome(runner)}
                            >
                              {runner}
                            </div>
                          ))}
                        </div>
                        <div className={styles.selectedLabel}>
                          {selectedDisplay != null ? (
                            <>Result: <span className={styles.selectedValue}>{selectedDisplay}</span></>
                          ) : (
                            <span className={styles.selectedHint}>Select a runner</span>
                          )}
                        </div>
                      </>
                    ) : isThreeWay ? (
                      <>
                        <div className={styles.outcomeRow}>
                          <div
                            className={`${styles.outcomeBox} ${selectedOutcome === teamOne ? styles.selected : ''}`}
                            onClick={() => setSelectedOutcome(teamOne)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && setSelectedOutcome(teamOne)}
                          >
                            {teamOne}
                          </div>
                          <div
                            className={`${styles.outcomeBox} ${selectedOutcome === 'Draw' ? styles.selected : ''}`}
                            onClick={() => setSelectedOutcome('Draw')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && setSelectedOutcome('Draw')}
                          >
                            Draw
                          </div>
                          <div
                            className={`${styles.outcomeBox} ${selectedOutcome === teamTwo ? styles.selected : ''}`}
                            onClick={() => setSelectedOutcome(teamTwo)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && setSelectedOutcome(teamTwo)}
                          >
                            {teamTwo}
                          </div>
                        </div>
                        <div className={styles.selectedLabel}>
                          {selectedDisplay != null ? (
                            <>Result: <span className={styles.selectedValue}>{selectedDisplay}</span></>
                          ) : (
                            <span className={styles.selectedHint}>Select Team 1, Draw, or Team 2</span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={styles.outcomeRow}>
                          <div
                            className={`${styles.outcomeBox} ${selectedOutcome === teamOne ? styles.selected : ''}`}
                            onClick={() => setSelectedOutcome(teamOne)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && setSelectedOutcome(teamOne)}
                          >
                            {teamOne}
                          </div>
                          <span className={styles.vsLabel}>VS</span>
                          <div
                            className={`${styles.outcomeBox} ${selectedOutcome === teamTwo ? styles.selected : ''}`}
                            onClick={() => setSelectedOutcome(teamTwo)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && setSelectedOutcome(teamTwo)}
                          >
                            {teamTwo}
                          </div>
                        </div>
                        <div className={styles.selectedLabel}>
                          {selectedDisplay != null ? (
                            <>Result: <span className={styles.selectedValue}>{selectedDisplay}</span></>
                          ) : (
                            <span className={styles.selectedHint}>Select winner</span>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                {mode === 'declare' && !useTeamSelection && (
                  <div className={styles.winnerIdWrap}>
                    <label className={styles.winnerIdLabel}>
                      {isBinaryFancy ? 'Winner Name' : 'Winner id'}
                    </label>
                    {isBinaryFancy ? (
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          type="button"
                          className={`${styles.outcomeBox} ${winnerId === 'Yes' ? styles.selected : ''}`}
                          onClick={() => setWinnerId('Yes')}
                          style={{ flex: 1 }}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className={`${styles.outcomeBox} ${winnerId === 'No' ? styles.selected : ''}`}
                          onClick={() => setWinnerId('No')}
                          style={{ flex: 1 }}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <input
                        type="text"
                        className={styles.winnerIdInput}
                        value={winnerId}
                        onChange={(e) => setWinnerId(e.target.value)}
                        placeholder="Enter winner id"
                        autoFocus
                      />
                    )}
                  </div>
                )}

                {mode === 'void' && (
                  <div className={styles.voidMessage}>
                    Do you really want to cancel or refund the bets? All stakes will be refunded to the users. This action cannot be undone.
                  </div>
                )}

                <div className={styles.submitRow}>
                  <button
                    type="button"
                    className={`${styles.submitBtn} ${styles.submitBtnSecondary}`}
                    onClick={onHide}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`${styles.submitBtn} ${mode === 'void' ? styles.submitBtnDanger : styles.submitBtnPrimary}`}
                    disabled={isSubmitDisabled}
                  >
                    {submitting ? 'Submitting...' : mode === 'void' ? 'Confirm Void' : 'Submit'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default FancySettlementModal;
